import {RequestMethod} from '@gl/lit/common/constants/index.js';
import type {Dataset} from '@gl/lit/types/datasets.js';
import {FlyErrorWarning} from '@gl/lit/utils/errors.js';
import {fetcher} from '@gl/lit/utils/http.js';
import type {
  QuerviceDatasetSchema,
  QuerviceTableSchema,
} from '@gl/lit/types/quervice.js';

export enum QuerviceServiceSchemaBody {
  DATASET_GROUP = 'dataset-group',
  DATASET = 'dataset',
}

class QuerviceService {
  async fetchFullSchema(
    type: QuerviceServiceSchemaBody,
    id: number,
    datasets: Dataset[],
    audience_id?: string
  ): Promise<Map<number, QuerviceTableSchema> | void> {
    try {
      const body =
        type === QuerviceServiceSchemaBody.DATASET_GROUP
          ? {dataset_group_id: id, audience_id: audience_id}
          : {dataset_id: id, audience_id: audience_id};
      // TODO: Change type to {data: Array<{datasets: QuerviceDatasetSchema[]}>} when quervice multi-catalog is deployed
      const res = await fetcher<{
        data:
          | Array<{datasets: QuerviceDatasetSchema[]}>
          | {datasets: QuerviceDatasetSchema[]};
      }>('/api/v2/quervice/full_schema', {
        method: RequestMethod.POST,
        body: body,
      });
      if (!res) {
        throw Error('Invalid schema request via Quervice.');
      }

      // TODO: Remove ternary when quervice multi-catalog is deployed
      const returnedDatasets: QuerviceDatasetSchema[] = Array.isArray(res.data)
        ? res.data.flatMap(catalog => catalog.datasets)
        : res.data.datasets;

      if (!returnedDatasets) {
        throw Error('Invalid schema request via Quervice.');
      }

      const tableSchemaMap = new Map<number, QuerviceTableSchema>();
      datasets.forEach(dataset => {
        const schema = returnedDatasets.find(s => {
          return s.name === dataset.dataset_without_db;
        });

        if (!schema) return;

        const table = schema.tables.find(t => t.name === dataset.source_table);
        if (table) tableSchemaMap.set(dataset.id, table);
      });

      return tableSchemaMap;
    } catch (err) {
      console.error(err);
      const errorDesc =
        type === QuerviceServiceSchemaBody.DATASET_GROUP
          ? `dataset group: ${id}`
          : `dataset: ${id}`;
      new FlyErrorWarning(
        `Error fetching schema for ${errorDesc}`,
        {
          label: 'request',
          data: 'GET: /api/v2/quervice/full_schema',
        },
        {
          label: 'response',
          data: err,
        }
      ).register('Error AudienceBuilder');
    }
  }
}

export const querviceService = new QuerviceService();
